import { zonoApi } from '../api/zonoApi';
import {
  BulkProductUpdateRequest,
  CreateProductRequest,
  CreateProductResponse,
  GenerateCustomerPricingPDFRequest,
  GenerateCustomerPricingPDFResponse,
  GenerateProductCatalogPDFRequest,
  GenerateProductCatalogPDFResponse,
  getProductDetailsRequest,
  getProductDetailsResponse,
  ParseFileRequest,
  ParseImageToProductsRequest,
  ParseImageToProductsResponse,
  ProductListResponse,
  ProductsRequest,
} from './product-model';

const ProductApi = zonoApi.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.mutation<ProductListResponse, ProductsRequest>({
      query: (args) => ({
        url: `hub/commerce-v2/products/search/${args.workspaceId}`,
        method: 'POST',
        body: args.body,
        params: { ...args.params, sellerWorkspaceId: args.workspaceId },
      }),
    }),
    getProductsByCustomer: builder.mutation<
      ProductListResponse,
      ProductsRequest
    >({
      query: ({ body, workspaceId, customerId, ...args }) => ({
        url: `hub/commerce-v2/products/search/customer/${workspaceId}`,
        method: 'POST',
        body: body,
        customerId,
        params: { ...args.params, sellerWorkspaceId: workspaceId },
      }),
    }),
    parseFile: builder.mutation<string, ParseFileRequest>({
      query(arg) {
        const { sellerWorkspaceId, body } = arg;
        return {
          url: `hub/bot/api/v1/parse-file`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body: body,
        };
      },
      transformResponse: (response: { parsedText: string }) => {
        return response.parsedText;
      },
    }),
    parseImageToProducts: builder.mutation<
      ParseImageToProductsResponse,
      ParseImageToProductsRequest
    >({
      query(arg) {
        return {
          url: `hub/bot/api/v1/parse-products/${arg?.workspaceId}`,
          method: 'POST',
          params: {
            sellerWorkspaceId: arg?.sellerWorkspaceId,
            random: Math.random(),
          },
          body: arg?.body,
        };
      },
    }),
    getProductDetails: builder.mutation<
      getProductDetailsResponse,
      {
        sellerWorkspaceId: string;
        customerId: string;
        body: getProductDetailsRequest;
      }
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/products/search/customer/${arg.sellerWorkspaceId}?sellerWorkspaceId=${arg.sellerWorkspaceId}`,
          method: 'POST',
          params: {
            sellerWorkspaceId: arg?.sellerWorkspaceId,
            customerId: arg?.customerId,
            pageNo: 1,
            pageSize: 10,
          },
          body: arg?.body,
        };
      },
    }),
    createUpdateProduct: builder.mutation<
      CreateProductResponse,
      CreateProductRequest
    >({
      query: (args) => {
        const { workspaceId, ...body } = args;
        return {
          url: `hub/commerce-v2/products/${workspaceId}`,
          method: 'POST',
          body: body,
          params: { sellerWorkspaceId: workspaceId },
        };
      },
    }),
    bulkUpdateProducts: builder.mutation<
      CreateProductResponse[],
      BulkProductUpdateRequest
    >({
      query: ({ sellerWorkspaceId, ...body }) => ({
        url: `hub/commerce-v2/products/bulk/${sellerWorkspaceId}`,
        method: 'POST',
        body,
        params: { sellerWorkspaceId },
      }),
    }),
    generateProductCatalogPDF: builder.mutation<
      GenerateProductCatalogPDFResponse,
      GenerateProductCatalogPDFRequest
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/catalogue/generate/pdf/${arg.params.sellerWorkspaceId}`,
          method: 'POST',
          params: arg.params,
          body: arg.body,
          responseHandler: (response) => response.text(),
        };
      },
    }),
    generateCustomerPricingProductPDF: builder.mutation<
      GenerateCustomerPricingPDFResponse,
      GenerateCustomerPricingPDFRequest
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/catalogue/customer/generate/pdf/${arg.params.sellerWorkspaceId}`,
          method: 'POST',
          params: arg.params,
          body: arg.body,
          responseHandler: (response) => response.text(),
        };
      },
    }),
  }),
});

export const {
  useGetProductsMutation,
  useGetProductsByCustomerMutation,
  useParseFileMutation,
  useParseImageToProductsMutation,
  useGetProductDetailsMutation,
  useCreateUpdateProductMutation,
  useBulkUpdateProductsMutation,
  useGenerateProductCatalogPDFMutation,
  useGenerateCustomerPricingProductPDFMutation,
} = ProductApi;
